// ActivityCommDashboard.js - Landing Page for Activity & Communications Module

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardActions,
  Button, Chip, Avatar, Divider, CircularProgress, Alert, Stack,
  IconButton
} from '@mui/material';
import {
  Assignment as TaskIcon,
  PersonPin as MyTasksIcon,
  CalendarMonth as CalendarIcon,
  EventAvailable as BookingIcon,
  Email as EmailIcon,
  Forum as CommIcon,
  AccountTree as WorkflowIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { taskService, communicationService } from './activityCommService';

const ActivityCommDashboard = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [taskStats, setTaskStats] = useState({});
  const [commStats, setCommStats] = useState({});
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    document.title = 'Activity & Communications | Activity & Communications';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadDashboard();
    }
  }, [canView, chatbotId]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};

      const [taskRes, commRes] = await Promise.allSettled([
        taskService.getStats(params),
        communicationService.getStats(params)
      ]);

      if (taskRes.status === 'fulfilled') {
        setTaskStats(taskRes.value.data || taskRes.value || {});
      } else {
        console.error('Task stats fetch failed:', taskRes.reason);
      }

      if (commRes.status === 'fulfilled') {
        setCommStats(commRes.value.data || commRes.value || {});
      } else {
        console.error('Communication stats fetch failed:', commRes.reason);
      }
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, mt: { xs: 2, sm: 10 } }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view the Activity & Communications module.
      </Alert>
    );
  }

  const statCards = [
    { label: 'Total Tasks', value: taskStats.total_tasks || 0, color: '#1976d2' },
    { label: 'Overdue Tasks', value: taskStats.overdue_tasks || 0, color: '#d32f2f' },
    { label: 'Due Today', value: taskStats.due_today || 0, color: '#ed6c02' },
    { label: 'This Week', value: taskStats.this_week || 0, color: '#2e7d32' },
    { label: 'Total Emails', value: commStats.total_emails || 0, color: '#9c27b0' },
    { label: 'Total Calls', value: commStats.total_calls || 0, color: '#0288d1' },
    { label: 'Total SMS', value: commStats.total_sms || 0, color: '#00897b' }
  ];

  const navCards = [
    {
      title: 'Tasks',
      icon: <TaskIcon sx={{ fontSize: 60, color: '#1976d2' }} />,
      link: `/chatbot/${chatbotId}/crm/activity/tasks`,
      color: '#1976d2',
      count: taskStats.total_tasks || 0,
      description: 'Manage and track all tasks across your team.'
    },
    {
      title: 'My Tasks',
      icon: <MyTasksIcon sx={{ fontSize: 60, color: '#7b1fa2' }} />,
      link: `/chatbot/${chatbotId}/crm/activity/my-tasks`,
      color: '#7b1fa2',
      count: taskStats.my_tasks || 0,
      description: 'View tasks assigned to you.'
    },
    {
      title: 'Calendar',
      icon: <CalendarIcon sx={{ fontSize: 60, color: '#2e7d32' }} />,
      link: `/chatbot/${chatbotId}/crm/activity/calendar`,
      color: '#2e7d32',
      count: taskStats.upcoming_events || 0,
      description: 'View and manage calendar events and meetings.'
    },
    {
      title: 'Booking Pages',
      icon: <BookingIcon sx={{ fontSize: 60, color: '#ed6c02' }} />,
      link: `/chatbot/${chatbotId}/crm/activity/booking-pages`,
      color: '#ed6c02',
      count: taskStats.booking_pages || 0,
      description: 'Configure shareable booking pages for appointments.'
    },
    {
      title: 'Email Sync',
      icon: <EmailIcon sx={{ fontSize: 60, color: '#0288d1' }} />,
      link: `/chatbot/${chatbotId}/crm/activity/email-sync`,
      color: '#0288d1',
      count: commStats.total_emails || 0,
      description: 'Sync and track email communications.'
    },
    {
      title: 'Communications',
      icon: <CommIcon sx={{ fontSize: 60, color: '#c62828' }} />,
      link: `/chatbot/${chatbotId}/crm/activity/communications`,
      color: '#c62828',
      count: (commStats.total_emails || 0) + (commStats.total_calls || 0) + (commStats.total_sms || 0),
      description: 'Unified view of all communication logs.'
    },
    {
      title: 'Workflows',
      icon: <WorkflowIcon sx={{ fontSize: 60, color: '#6a1b9a' }} />,
      link: `/chatbot/${chatbotId}/crm/activity/workflows`,
      color: '#6a1b9a',
      count: null,
      description: 'Automate contact reminders, follow-ups, and communication workflows.'
    }
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/activity-dashboard`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              <DashboardIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Activity & Communications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage tasks, calendar events, bookings, and communications
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Row */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={6} sm={4} md={12 / 7} key={index}>
            <Paper
              sx={{
                p: 2,
                textAlign: 'center',
                borderTop: `3px solid ${stat.color}`
              }}
            >
              <Typography variant="h4" fontWeight={700} color={stat.color}>
                {stat.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stat.label}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Navigation Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {navCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: '0.3s',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)'
                }
              }}
              onClick={() => navigate(card.link)}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 4 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: `${card.color}15`,
                    margin: '0 auto 16px'
                  }}
                >
                  {card.icon}
                </Avatar>
                <Typography variant="h5" gutterBottom fontWeight={600}>
                  {card.title}
                </Typography>
                <Chip
                  label={card.count}
                  sx={{ mb: 1, bgcolor: `${card.color}15`, color: card.color, fontWeight: 700, fontSize: '1rem' }}
                />
                <Typography variant="body2" color="text.secondary">
                  {card.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ bgcolor: card.color }}
                >
                  Open
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ActivityCommDashboard;
